// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { QuoteCacheEventType } from './QuoteCache';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { TradableIdRouteId } from '@shared/utils/Instruments/TradableIdRouteId';
import { Connection } from '../Connection';
export class QuoteRequestListener {
    constructor(quoteCache) {
        this.TIMER_TICK_MS = 100;
        this.UPDATE_TICK_MS = 10000;
        this._timerElapsedMS = 0;
        this._quoteRequestSubscribers = new Map();
        this._newQuoteRequestSubscribers = [];
        this._activeSubscribers = new Map();
        this.onSubscribeInstrument = (instrument, quoteType) => {
            if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
                return;
            }
            const quoteRouteId = this.getQuoteRouteId(instrument);
            if (!this._activeSubscribers.get(quoteRouteId)) {
                this._activeSubscribers.set(quoteRouteId, []);
            }
            const subscribers = this._activeSubscribers.get(quoteRouteId);
            if (subscribers.indexOf(instrument.InstrumentTradableID) === -1) {
                subscribers.push(instrument.InstrumentTradableID);
            }
        };
        this.onUnsubscribeInstrument = (instrument, quoteType) => {
            if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
                return;
            }
            const quoteRouteId = this.getQuoteRouteId(instrument);
            const subscribers = this._activeSubscribers.get(quoteRouteId);
            if (isValidArray(subscribers)) {
                const index = subscribers.indexOf(instrument.InstrumentTradableID);
                if (index === -1) {
                    return;
                }
                subscribers.splice(index, 1);
                if (subscribers.length === 0) {
                    this._activeSubscribers.delete(quoteRouteId);
                }
            }
        };
        this.tick = () => {
            this._timerElapsedMS += this.TIMER_TICK_MS;
            this.sendQuoteRequestOnSubscribe();
            if (this._timerElapsedMS >= this.UPDATE_TICK_MS) {
                this.sendQuoteRequestOnUpdate();
                this._timerElapsedMS = 0;
            }
        };
        this._timer = setInterval(this.tick, this.TIMER_TICK_MS);
        this._quoteCache = quoteCache;
        this._quoteCache.subscribeEvent(QuoteCacheEventType.OnSubscribeInstrument, this.onSubscribeInstrument);
        this._quoteCache.subscribeEvent(QuoteCacheEventType.OnUnsubscribeInstrument, this.onUnsubscribeInstrument);
    }
    Subscribe(instrument, listenerObject) {
        const tradableId = instrument.InstrumentTradableID;
        const quoteRouteId = this.getQuoteRouteId(instrument);
        let subscribersByRouteId = this._quoteRequestSubscribers.get(quoteRouteId);
        if (isNullOrUndefined(subscribersByRouteId)) {
            subscribersByRouteId = new Map();
            this._quoteRequestSubscribers.set(quoteRouteId, subscribersByRouteId);
        }
        let subscribersByRouteIdAndTradableId = subscribersByRouteId.get(tradableId);
        if (isNullOrUndefined(subscribersByRouteIdAndTradableId)) {
            subscribersByRouteIdAndTradableId = [];
            subscribersByRouteId.set(tradableId, subscribersByRouteIdAndTradableId);
        }
        if (subscribersByRouteIdAndTradableId.length === 0 && !this.isExistActiveSubscriber(quoteRouteId, tradableId)) {
            this._newQuoteRequestSubscribers.push(new TradableIdRouteId(tradableId, quoteRouteId));
        }
        subscribersByRouteIdAndTradableId.push(listenerObject);
    }
    Unsubscribe(instrument, listenerObject) {
        const tradableId = instrument.InstrumentTradableID;
        const quoteRouteId = this.getQuoteRouteId(instrument);
        const subscribersByRouteId = this._quoteRequestSubscribers.get(quoteRouteId);
        if (isNullOrUndefined(subscribersByRouteId)) {
            return;
        }
        const subscribersByRouteIdAndTradableId = subscribersByRouteId.get(tradableId);
        if (isNullOrUndefined(subscribersByRouteIdAndTradableId)) {
            return;
        }
        subscribersByRouteIdAndTradableId.splice(subscribersByRouteIdAndTradableId.indexOf(listenerObject), 1);
        if (subscribersByRouteIdAndTradableId.length === 0) {
            subscribersByRouteId.delete(tradableId);
            if (subscribersByRouteId.size === 0) {
                this._quoteRequestSubscribers.delete(quoteRouteId);
            }
        }
    }
    Clear() {
        this._activeSubscribers.clear();
        this._quoteRequestSubscribers.clear();
    }
    destroy() {
        clearInterval(this._timer);
        this.Clear();
    }
    isExistActiveSubscriber(routeId, tradableId) {
        const subscribers = this._activeSubscribers.get(routeId);
        return isValidArray(subscribers) && subscribers.indexOf(tradableId) !== -1;
    }
    sendQuoteRequestOnSubscribe() {
        if (this._newQuoteRequestSubscribers.length == 0)
            return;
        const copyNewQuoteRequestSubscribers = [...this._newQuoteRequestSubscribers];
        this._newQuoteRequestSubscribers = [];
        this.sendRequest(copyNewQuoteRequestSubscribers);
    }
    sendQuoteRequestOnUpdate() {
        if (this._quoteRequestSubscribers.size == 0) {
            return;
        }
        const tradableIdRouteIdArray = [];
        for (const [routeId, tradableIdMap] of this._quoteRequestSubscribers) {
            for (const [tradableId, _] of tradableIdMap) {
                if (this.isExistActiveSubscriber(routeId, tradableId)) {
                    continue;
                }
                tradableIdRouteIdArray.push(new TradableIdRouteId(tradableId, routeId));
            }
        }
        this.sendRequest(tradableIdRouteIdArray);
    }
    sendRequest(tradableIdRouteIdArray) {
        if (!isValidArray(tradableIdRouteIdArray)) {
            return;
        }
        Connection.vendor.SendQuotesRequest(tradableIdRouteIdArray);
    }
    getQuoteRouteId(instrument) {
        const route = this._quoteCache.DataCache.Routes.getRouteByName(instrument.Route);
        return !isNullOrUndefined(route) ? route.QuoteRouteId : instrument.Route;
    }
}
